import { Theme, useTheme } from '@mui/material';
import { ConversationStatus } from '@provision/entity-types';
import { gql, QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { type IconLibrary } from 'src/components/DynamicIcon/dynamic-icon';
import { QueryVariables, Records } from 'src/graphql/types';
import { getThemeColor } from 'src/utils/strings';

interface GetConversationStatusQuery {
  getConversationStatus: Records<ConversationStatus>;
}

const GET_CONVERSATION_STATUS_QUERY = gql`
  query GetConversationStatus($params: QueryParams) @entity(name: "ConversationStatus") {
    getConversationStatus(queryParams: $params) {
      records {
        id
        name
        sortOrder
      }
    }
  }
`;

export const useConversationStatusList = (
  options?: QueryHookOptions<GetConversationStatusQuery>,
) => {
  const { data, ...rest } = useQuery<GetConversationStatusQuery, QueryVariables>(
    GET_CONVERSATION_STATUS_QUERY,
    {
      ...options,
      variables: {
        params: {
          sorting: { attribute: 'sortOrder', desc: false },
        },
      },
      context: {
        includePlatformLevelData: true,
      },
    },
  );

  return {
    conversationStatusList: data?.getConversationStatus.records,
    ...rest,
  };
};

export type StatusDisplayProps = {
  icon: string;
  library: IconLibrary;
  color: string;
};

export const statusDisplayPropsMap = {
  new: { icon: 'sparkle-circle', library: 'custom', color: 'custom.OV.sys.primary' },
  open: { icon: 'circle-play', library: 'lucide', color: 'custom.OV.sys.primary' },
  snoozed: { icon: 'alarm-clock', library: 'lucide', color: 'custom.OV.sys.secondary' },
  on_hold: { icon: 'circle-pause', library: 'lucide', color: 'custom.OV.sys.secondary' },
  solved: { icon: 'circle-check', library: 'lucide', color: 'custom.OV.sys.tertiary' },
  closed: { icon: 'lock', library: 'lucide', color: 'custom.OV.sys.tertiary' },
  default: { icon: 'circle', library: 'lucide', color: 'custom.OV.sys.onSurface' },
} as const satisfies Record<string, StatusDisplayProps>;

export type StatusId = keyof typeof statusDisplayPropsMap;

export function getStatusDisplay(statusId?: string | null): StatusDisplayProps {
  if (statusId && statusId in statusDisplayPropsMap) {
    return statusDisplayPropsMap[statusId as StatusId];
  }
  return statusDisplayPropsMap.default;
}

export type ConversationStatusOption = Pick<
  ConversationStatus,
  'id' | 'sortOrder' | 'name'
> & {
  label: ConversationStatus['name'];
  value: ConversationStatus['id'];
  icon: string;
  iconLibrary: IconLibrary;
  color: string;
};

export const useConversationStatusOptions = (
  options?: QueryHookOptions<GetConversationStatusQuery>,
) => {
  const { conversationStatusList } = useConversationStatusList(options);
  const theme = useTheme();

  return useMemo<ConversationStatusOption[]>(() => {
    if (!conversationStatusList?.length) {
      return [];
    }

    return conversationStatusList.map((item) => {
      const display = getStatusDisplay(item.id);
      return {
        id: item.id,
        value: item.id,
        name: item.name,
        label: item.name,
        sortOrder: item.sortOrder,
        icon: display.icon,
        iconLibrary: display.library,
        color: getStatusColor(item.id, theme),
      };
    });
  }, [conversationStatusList, theme]);
};

export function getStatusColor(item: string, theme: Theme): string {
  return getThemeColor(getStatusDisplay(item).color, theme);
}

export const DEFAULT_STATUS = {
  id: 'default',
  sortOrder: 0,
  value: 'default',
  label: 'Default',
  name: 'Default',
  icon: statusDisplayPropsMap.default.icon,
  iconLibrary: statusDisplayPropsMap.default.library,
  color: 'inherit',
} satisfies ConversationStatusOption;
